import { AxiosResponse } from 'axios';
import { LdJsonBreadCrumbsResort, LdJsonResortOverview, LdJsonSkiResort } from 'components/LdJson';
import { RouteAndAnalyticsProps } from 'page-components/pathTypes';
import { IResortLayout } from 'page-components/resort/_layout';
import React from 'react';
import { Routes } from 'routes';
import { getResortSlug } from 'util/memcache';
import { HeaderNavCurLink } from 'components/common/HeaderNav';
import {
  fetchContactInformation,
  fetchResortAccommodations,
  fetchResortDescriptionTabs,
  fetchResortLastFHReport, fetchResortPhotos, fetchResortRelated,
  fetchResortRelatedNews,
  fetchResortReviews,
  fetchResortReviewsPages,
  fetchResortShortInfo, fetchResortShortWeatherReport,
  fetchResortSnowReport,
  fetchResortStrengths,
  fetchResortTrailMap,
  fetchSnowfallInfo,
} from 'api/resort/static';
import {
  HistoricalSnowfallType,
  IContactInformation,
  IDescriptionsResort,
  IFirstHandReport,
  IRelatedNews,
  IResortPageInfo,
  IResortPageSnowInfo,
  IResortPathInfo,
  IResortReviews,
  IResortStrengthsOverview,
  IResortTrailmapPage,
  IReviewsGeneral,
  ISnowfallItem,
  ReviewsOrderBy,
} from 'api/resort/types';
import { CurrentLink, CurrentTab } from 'components/resort/ResortHeaderNav/constants';
import { Main } from 'components/resort/Layout/Main';
import PlanYourTrip from 'components/common/PlanYourTrip/ResortWidget';

import { TerrainOverview } from 'components/resort/TerrainDetailed';
import { TotalLifts } from 'components/resort/TotalLifts';
import { Hero400Error } from 'components/400';
import { fetchResortMeta } from 'api/region/static';
import { assertShouldBeNot404, wrapServerSideProps, wrapStaticProps } from 'util/wrapProps';
import { useSanitizedHtmlProps } from 'util/sanitized';
import { IAccommodationPage } from 'api/tripadvisor/types';
import { ResortPhotosData } from 'components/widget/types';
import { fetchNearbyResorts } from 'api/home/static';
import { INearbyResort } from 'api/region/types';
import { ResortGeneralInfo } from 'components/resort/ResortGeneralInfo';
import { OverviewTopSlider } from 'components/resort/OverviewTopSlider';
import { ImagesSection } from 'components/resort/ImagesSection';
import { WhereToStaySection } from 'components/resort/WhereToStaySection';
import { HowToGetThereSection } from 'components/resort/HowToGetThereSection';
import { WhenToGoSection } from 'components/resort/WhenToGoSection';
import { InsideScoopSection } from 'components/resort/InsideScoopSection';
import { ReviewsSection } from 'components/resort/ReviewsSection';
import { NearbyResortsSection } from 'components/resort/NearbyResortsSection';
import { useIsPermanentlyClosed } from 'util/useIsPermanentlyClosed';
import { SidebarAd } from 'googlead/components/SidebarAd';
import { hasWeSkiIntegration } from 'components/common/PlanYourTrip/WeSki/resolver';
import SurfaceRelatedArticles from 'components/resort/SurfaceRelatedArticles';
import { AiAssistantWidget } from 'components/common/AiAssistantWidget';
import styles from './ski-resort.module.scss';

const filterOutTabData = (data?: {
  dangerouslySetInnerHTML: {
    __html: string;
  };
}) => {
  if (!data) {
    return data;
  }
  const FORBIDDEN_CHARACTERS = '►';
  const result = data;

  // eslint-disable-next-line no-underscore-dangle
  let html = data.dangerouslySetInnerHTML.__html;
  const forbiddenCharacters = FORBIDDEN_CHARACTERS.split('');
  forbiddenCharacters.forEach((char) => {
    html = html.replace(new RegExp((char), 'g'), '');
  });
  // eslint-disable-next-line no-underscore-dangle
  result.dangerouslySetInnerHTML.__html = html;
  return result;
};

export type ResortOverviewPageProps = {
  resort: IResortPageInfo,
  fullResort: IResortPageSnowInfo,
  pathInfo: IResortPathInfo,
  resortStrengths: IResortStrengthsOverview,
  resortDescriptionTabs: Array<IDescriptionsResort>,
  snowfallInfoAnnual: Array<ISnowfallItem> | null,
  snowfallInfoMonthly: Array<ISnowfallItem> | null,
  trailmap: IResortTrailmapPage,
  contactInformation: IContactInformation,
  reviewsGeneral: IReviewsGeneral | null
  relatedNews: IRelatedNews | null,
  initials: {
    firstHand: IFirstHandReport | null,
  },
  accommodations: IAccommodationPage | null,
  photos: ResortPhotosData,
  nearbyResorts: INearbyResort[],
  resortReviews: IResortReviews
} & RouteAndAnalyticsProps & IResortLayout;

export const DEBUG_TAG = 'resort-overview-page';
export const TEMPLATE_ID = '3.2.1';
export const TEMPLATE_NAME = 'Resort Overview Resort';

export default function ResortOverviewPage({
  fullResort,
  pathInfo,
  resortStrengths,
  resortDescriptionTabs,
  reviewsGeneral,
  trailmap,
  contactInformation,
  accommodations,
  photos,
  nearbyResorts,
  snowfallInfoAnnual,
  snowfallInfoMonthly,
  resortReviews,
  relatedNews,
}: ResortOverviewPageProps) {
  const resortCountry = fullResort?.region?.country_code;

  const featuredImage = { cdnUrl: fullResort.image, name: fullResort.title };

  const topSliderPhotos = [featuredImage, ...photos];

  const tabs = (resortDescriptionTabs.reduce((prev: Record<string, string>, curr) => ({ ...prev, [curr.tab]: (curr.description || '') }), {}));

  const isMappedToWeSki = !!hasWeSkiIntegration({ resortId: fullResort.uuid });

  const resortDescription = useSanitizedHtmlProps(tabs.resort_profile ?? '');

  const lodgingProfile = useSanitizedHtmlProps(tabs.lodging_profile ?? '');

  const transportationDescription = useSanitizedHtmlProps(tabs.transportation_profile ?? '');

  const mountainOverview = useSanitizedHtmlProps(tabs.mountain_overview ?? '');

  const insideScoop = useSanitizedHtmlProps(tabs.inside_scoop ?? '');

  const { city } = contactInformation.address;
  const { country } = contactInformation.address;

  const totalSnowfall = snowfallInfoAnnual?.reduce((prev, curr) => prev + curr.totalSnow, 0);

  const annualSnowfall = totalSnowfall && snowfallInfoAnnual && Math.ceil(totalSnowfall / snowfallInfoAnnual?.length);

  if (!fullResort) {
    return <Hero400Error light />;
  }

  const isPermanentlyClosed = useIsPermanentlyClosed(fullResort);

  return (
    <>
      <Main>
        <div className={styles.container}>

          <div className={styles.wrapper}>
            {trailmap && resortStrengths
              && fullResort.forecast && (
                <div className={styles.flex_1}>
                  <ResortGeneralInfo
                    trailmap={trailmap}
                    resortStrengths={resortStrengths}
                    snowfallInfoMonthly={snowfallInfoMonthly}
                    snowfallInfoAnnual={snowfallInfoAnnual}
                    pathInfo={pathInfo}
                    annualSnowfall={annualSnowfall ?? undefined}
                  />
                  <div className={styles.ad_container}>
                    <div className={styles.news_container}>
                      <SurfaceRelatedArticles
                        relatedNewsList={relatedNews}
                        isFresh
                      />
                    </div>
                    <SidebarAd id={pathInfo.uuid} />
                  </div>
                </div>
            )}
            <section id={styles.main_content}>
              {photos && resortDescription && (
                <OverviewTopSlider
                  photos={topSliderPhotos}
                  resortDescription={filterOutTabData(resortDescription)}
                />
              )}

              {photos && photos.length > 2 && (
                <ImagesSection photos={photos.slice(1)} />
              )}
              <AiAssistantWidget
                templateId={TEMPLATE_ID}
                name={fullResort.title}
                margin={{ marginTop: '-1.75rem', marginBottom: '1.75rem' }}
              />

              {lodgingProfile && (
                <WhereToStaySection
                  lodgingProfile={filterOutTabData(lodgingProfile)}
                  accommodations={accommodations}
                  isMappedToWeSki={isMappedToWeSki}
                  resortId={fullResort.uuid}
                />
              )}

              <PlanYourTrip
                resort={pathInfo}
                hasSkiRental={pathInfo.hasSkiRental}
                hasFlights={pathInfo.hasFlights}
                hasCarRental={pathInfo.hasCarRental}
                liftsUrl={fullResort.liftsUrl}
                rentalUrl={fullResort.rentalUrl}
                lessonsUrl={fullResort.lessonsUrl}
                isPartner={fullResort.isPartner}
                resortCountry={resortCountry}
              />

              {transportationDescription && contactInformation && (
                <HowToGetThereSection
                  city={city}
                  contactInformation={contactInformation}
                  country={country}
                  description={filterOutTabData(transportationDescription)}
                  fullResort={fullResort}
                />
              )}

              {trailmap && trailmap.dates && (
                <WhenToGoSection
                  mountainOverview={mountainOverview}
                  trailmap={trailmap}
                  isPermanentlyClosed={isPermanentlyClosed}
                  annualSnowfall={annualSnowfall ?? undefined}
                />
              )}
              {trailmap && (
                <TerrainOverview
                  isFresh
                  data={trailmap}
                  pathInfo={pathInfo}
                  resortCountry={resortCountry}
                />
              )}
              {trailmap && <TotalLifts lifts={trailmap.lifts} />}

              {insideScoop && (
                <InsideScoopSection paragraphs={filterOutTabData(insideScoop)} photos={photos.slice(4)} />
              )}

              {resortReviews.pagination.count > 0 && (
                <ReviewsSection pathInfo={pathInfo} resortReviews={resortReviews} />
              )}

              {nearbyResorts
                && <NearbyResortsSection nearbyResorts={nearbyResorts.slice(1)} />}
            </section>

          </div>
        </div>

      </Main>
      <LdJsonResortOverview
        resort={fullResort}
        pathInfo={pathInfo}
        trailmap={trailmap}
        reviewsGeneral={reviewsGeneral}
        resortDescriptionTabs={resortDescriptionTabs}
        snowfallInfoAnnual={snowfallInfoAnnual}
      />
      <LdJsonSkiResort
        contact={contactInformation}
        awards={resortStrengths}
        resort={fullResort}
        averageReviews={reviewsGeneral}
      />
      <LdJsonBreadCrumbsResort
        resort={pathInfo}
        region={fullResort.region}
        fn={Routes.resortTripPlanningOverview}
      />
    </>
  );
}

export const fetchProps = async ({ resort }: { region: string, resort: string }): Promise<ResortOverviewPageProps> => {
  const cache: AxiosResponse<IResortPathInfo> = await assertShouldBeNot404(() => getResortSlug(resort));
  const { uuid } = cache.data;
  const res = await fetchResortShortInfo(uuid);
  const fullResort = await fetchResortSnowReport(uuid);
  const resortStrengths = await fetchResortStrengths(uuid);
  const resortDescriptionTabs = await fetchResortDescriptionTabs(uuid);
  const reviewsGeneral = await fetchResortReviews(uuid);
  const snowfallInfoAnnual = await fetchSnowfallInfo(uuid, HistoricalSnowfallType.annual);
  const snowfallInfoMonthly = await fetchSnowfallInfo(uuid, HistoricalSnowfallType.monthly);
  const trailmap = await fetchResortTrailMap(uuid);
  const contactInformation = await fetchContactInformation(uuid);
  const relatedNews = await fetchResortRelatedNews(uuid);
  const accommodations = await fetchResortAccommodations(uuid);
  const photos = await fetchResortPhotos(uuid);
  const nearbyResorts = await fetchNearbyResorts(fullResort.latitude, fullResort.longitude);
  const resortReviews = await fetchResortReviewsPages(uuid, ReviewsOrderBy.date, 0);

  return {
    type: 'resort',
    route: 'ski-resort',
    resort: res,
    shortWeather: await fetchResortShortWeatherReport(uuid),
    fullResort,
    resortStrengths,
    resortDescriptionTabs,
    reviewsGeneral,
    relatedNews,
    snowfallInfoAnnual: snowfallInfoAnnual ?? null,
    snowfallInfoMonthly: snowfallInfoMonthly ?? null,
    trailmap,
    contactInformation,
    pathInfo: cache.data,
    initials: {
      firstHand: await fetchResortLastFHReport(uuid),
    },

    /**
       * Layout
       */
    related: await fetchResortRelated(uuid),
    navigation: {
      link: CurrentLink.OVERVIEW,
      tab: CurrentTab.TRIP_PLANNING,
      head: HeaderNavCurLink.TRIP_PLANNING,
    },
    templateName: TEMPLATE_NAME,
    templateId: TEMPLATE_ID,
    adTKey: 'resort profile',
    hero: {
      title: 'resort.overview.heroTitle',
      showVideo: true,
    },
    accommodations,
    meta: {
      description: 'Resort.profile.description',
      redirectTo: cache.data.domain ?? null,
      title: 'Resort.profile.title',
      dynamic: (await fetchResortMeta(uuid)),
    },
    photos,
    nearbyResorts,
    resortReviews,
  };
};

// This function gets called at build time
export const getResortOverviewPageStaticProps = wrapStaticProps(fetchProps);

export const getResortOverviewPageServerSideProps = wrapServerSideProps(fetchProps);
